import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { epicengageSupabasev1 } from '../integrations/supabase/client';
import { useAuth } from './useEngageV1Auth';

type Merchant = {
  id: string;
  name: string;
  business_type: string | null;
  region: string | null;
  brand_tone: string | null;
  sender_email: string | null;
  sender_domain: string | null;
  ses_verified: boolean | null;
};

type Credits = {
  credits_total: number;
  credits_used: number;
  reset_at: string | null;
  plan_name: string | null;
};

type MerchantContextValue = {
  merchant: Merchant | null;
  credits: Credits | null;
  loading: boolean;
  refresh: () => Promise<void>;
};

const MerchantContext = createContext<MerchantContextValue>({
  merchant: null,
  credits: null,
  loading: true,
  refresh: async () => {},
});

export const MerchantProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setMerchant(null);
      setCredits(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data: membership } = await epicengageSupabasev1
      .from("user_merchants")
      .select("merchant_id, merchants(id, name, business_type, region, brand_tone, sender_email, sender_domain, ses_verified)")
      .eq("user_id", user.id)
      .limit(1)
      .maybeSingle();
    console.log(membership, 'membershipmembershipmembership')
    const m = (membership as { merchants?: Merchant | null } | null)?.merchants ?? null;
    setMerchant(m);

    if (m) {
      const { data: c } = await epicengageSupabasev1
        .from("email_credits")
        .select("credits_total, credits_used, reset_at, plan_name")
        .eq("merchant_id", m.id)
        .maybeSingle();
      setCredits(
        c
          ? {
              credits_total: c.credits_total ?? 0,
              credits_used: c.credits_used ?? 0,
              reset_at: c.reset_at,
              plan_name: c.plan_name,
            }
          : null
      );
    } else {
      setCredits(null);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <MerchantContext.Provider value={{ merchant, credits, loading, refresh: load }}>
      {children}
    </MerchantContext.Provider>
  );
};

export const useMerchant = () => useContext(MerchantContext);
